/**
 * Cross-tab auth coordination. The access token itself is already shared
 * via localStorage, whose native `storage` event *would* fire here -- but
 * it only carries a raw string, with no room for the message shapes this
 * needs (`logout`, `claimed` with a path). BroadcastChannel carries
 * structured, typed messages instead, and it's what the rest of the app
 * already uses for this purpose (see the WebSocket-connection story in
 * `websocket/storage.ts`, which listens for the native event on a
 * different key and isn't a fit for this).
 */

export type AuthBroadcastMessage =
  | { type: "token"; access: string }
  | { type: "logout" }
  | { type: "claimed"; path: string };

const CHANNEL_NAME = "planmat-auth";

let channel: BroadcastChannel | null = null;

const getChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!channel) channel = new BroadcastChannel(CHANNEL_NAME);
  return channel;
};

export const publishAuthMessage = (message: AuthBroadcastMessage): void => {
  getChannel()?.postMessage(message);
};

/** Returns an unsubscribe function. No-ops (and never calls `handler`) in
 * environments without BroadcastChannel support. */
export const subscribeToAuthBroadcast = (
  handler: (message: AuthBroadcastMessage) => void
): (() => void) => {
  const ch = getChannel();
  if (!ch) return () => {};

  const listener = (event: MessageEvent<AuthBroadcastMessage>) => handler(event.data);
  ch.addEventListener("message", listener);
  return () => ch.removeEventListener("message", listener);
};
